import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { pickBy } from 'lodash';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';

import { ModalCard } from 'partial/components/Modal';
import FormInput from 'partial/components/form/FormInput';
import AsyncButton from 'modules/common/components/AsyncButton';
import InputNumber from 'modules/access-control/user-management/components/InputNumber';
import Password from 'modules/access-control/user-management/components/Password';
import { useAddOrganization, useEditOrganization } from '../hooks';

const AddUpdateOrganization = ({ onClose, type, prevData }) => {
  const navigate = useNavigate();
  const { values, setFieldValue } = useFormik({
    initialValues: {
      organization_name: prevData?.email || '',
      first_name: prevData?.profile?.first_name || '',
      last_name: prevData?.profile?.last_name || '',
      email: prevData?.email || '',
      mobile_number: prevData?.profile?.mobile_number || '',
      password: '',
      new_password_confirmation: '',
    },
  });

  const password = values?.password;
  const passwordConfirm = values?.new_password_confirmation;

  const testPassRegex = useMemo(() => {
    const pattern =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{10,}$/;
    return pattern?.test(password);
  }, [password]);

  const validatePass = useMemo(() => {
    const validate =
      !testPassRegex || !password?.length || !passwordConfirm?.length;
    return type === 'add' ? validate : false;
  }, [password, passwordConfirm, testPassRegex, type]);

  const isFieldEmpty = useMemo(
    () =>
      !values?.first_name?.length ||
      !values?.last_name?.length ||
      !values?.email?.length ||
      !values?.mobile_number?.length,

    [values],
  );

  const onSuccess = () => {
    if (type === 'add') {
      navigate('/subscribers/onboarding');
    }
    onClose();
  };

  const [isAddLoading, add] = useAddOrganization(onSuccess);
  const [isEditLoading, editUser] = useEditOrganization(onSuccess);

  const isLoading = isAddLoading || isEditLoading;

  const handleSubmit = (e) => {
    e.preventDefault();

    if (type === 'add')
      add(
        pickBy({
          ...values,
          password_confirmation: password,
          mobile_number_country_code: 'PH',
          new_password_confirmation: '',
        }),
      );
    if (type === 'edit')
      editUser(
        pickBy({
          ...values,
          id: prevData?.uuid,
          email: '',
          new_password_confirmation: '',
        }),
      );
  };

  return (
    <ModalCard
      size="sm"
      title={`${type === 'add' ? 'ADD' : 'EDIT'} AGENCY`}
      onClose={onClose}
      containerClassName="max-h-[95vh] overflow-auto"
    >
      <form className="px-4 py-6 " onSubmit={handleSubmit} autoComplete="off">
        <div className="space-y-4">
          <FormInput
            name="organization_name"
            onSetFieldValue={setFieldValue}
            value={values?.organization_name}
            label="Organization Name"
            type="text"
            required
            placeHolder="Enter organization name"
          />
          <FormInput
            name="first_name"
            onSetFieldValue={setFieldValue}
            value={values?.first_name}
            label="First Name"
            type="text"
            required
            placeHolder="Enter first name"
          />
          <FormInput
            name="last_name"
            onSetFieldValue={setFieldValue}
            value={values?.last_name}
            label="Last Name"
            type="text"
            required
            placeHolder="Enter last name"
          />
          <FormInput
            name="email"
            onSetFieldValue={setFieldValue}
            value={values?.email}
            label="Email"
            type="email"
            required
            placeHolder="Enter email"
          />

          <InputNumber
            name="mobile_number"
            onSetFieldValue={setFieldValue}
            value={values?.mobile_number}
            label="Mobile Number"
            type="text"
            required
          />
          {type === 'add' && (
            <Password
              password={password}
              passwordConfirm={passwordConfirm}
              onChange={setFieldValue}
              testPassRegex={testPassRegex}
              passName="password"
              passOnly={false}
            />
          )}
        </div>

        <div className="flex justify-end gap-2 mt-10">
          <AsyncButton
            color="outline"
            className="font-[400] !text-neutral-500"
            onClick={onClose}
          >
            Cancel
          </AsyncButton>
          <AsyncButton
            type="submit"
            className="font-[400]"
            color="outline"
            disabled={
              isFieldEmpty || validatePass || password !== passwordConfirm
            }
            isLoading={isLoading}
          >
            {type === 'add' ? 'add' : 'save'}
          </AsyncButton>
        </div>
      </form>
    </ModalCard>
  );
};

AddUpdateOrganization.defaultProps = {
  type: 'add',
  prevData: {},
};

AddUpdateOrganization.propTypes = {
  onClose: PropTypes.func.isRequired,
  type: PropTypes.string,
  prevData: PropTypes.instanceOf(Object),
};

export default AddUpdateOrganization;
